/**
 * Envia evento Meta CAPI ao mover negociação para etapa com conversion_event.
 * Sem UI; fire-and-forget; token só na edge.
 */
import { supabase } from '../../../lib/supabase';
import { createModuleLogger } from '../../../utils/logger';
import {
    getAttributionByLeadId,
    fetchConnectedIntegration,
    resolveMetaDatasetId,
} from './metaAttributionLookup';
import { type MetaAttribution } from './metaAttributionService';
import { resolveMetaWabaId } from '../metaAPI';
import { sendMetaConversionEvent } from './metaEventsService';
import {
    buildStageMetaEventId,
    shouldSendStageMetaEvent,
} from './metaStageConversionRules';

const log = createModuleLogger('[MetaStageCAPI]');

async function loadStageConversionEvent(
    stageId: string,
    organizationId: string,
): Promise<string | null> {
    const { data, error } = await supabase
        .from('funnel_stages')
        .select('conversion_event, organization_id')
        .eq('id', stageId)
        .maybeSingle();

    if (error || !data) {
        log.warn('Etapa não encontrada', { stageId });
        return null;
    }

    if (data.organization_id && data.organization_id !== organizationId) {
        log.warn('Etapa fora da organização', { stageId });
        return null;
    }

    return (data.conversion_event as string | null) ?? null;
}

async function resolveOrganizationId(
    leadId: string,
    organizationId?: string,
): Promise<string | null> {
    if (organizationId) return organizationId;
    const { data } = await supabase
        .from('opportunities')
        .select('organization_id')
        .eq('id', leadId)
        .maybeSingle();
    return (data?.organization_id as string | undefined) ?? null;
}

function alreadySentForStage(attribution: MetaAttribution | null | undefined, stageId: string): boolean {
    return Boolean(attribution?.stage_conversions_sent?.[stageId]);
}

/**
 * Se a etapa tiver Evento do Meta e houver CTWA ≤7d, envia CAPI uma vez.
 * Nunca lança para o caller — loga e retorna.
 */
export async function sendStageMetaConversionIfConfigured(
    leadId: string,
    stageId: string,
    organizationId?: string,
): Promise<void> {
    try {
        if (!leadId || !stageId) return;
        if (stageId === 'won' || stageId === 'lost') return;

        const orgId = await resolveOrganizationId(leadId, organizationId);
        if (!orgId) {
            log.info('Marco de funil não enviado', { reason: 'Sem organization_id' });
            return;
        }

        const conversionEvent = await loadStageConversionEvent(stageId, orgId);
        const attr = await getAttributionByLeadId(leadId, orgId);
        const a0 = attr.attribution ?? ({} as MetaAttribution);

        const decision = shouldSendStageMetaEvent({
            conversionEvent,
            ctwaClid: a0.ctwa_clid,
            capturedAt: a0.captured_at ?? null,
            firstTouchAt: attr.firstTouchAt,
            alreadySentForStage: alreadySentForStage(a0, stageId),
        });

        if (!decision.send) {
            log.info('Marco de funil não enviado', { reason: decision.reason });
            return;
        }

        if (!attr.phone) {
            log.info('Marco de funil não enviado', { reason: 'Lead sem telefone' });
            return;
        }

        const metaInteg = await fetchConnectedIntegration(orgId, 'meta_ads');
        const datasetId = resolveMetaDatasetId(a0, metaInteg);
        if (!datasetId) {
            log.info('Marco de funil não enviado', { reason: 'Sem dataset_id' });
            return;
        }

        const eventId = buildStageMetaEventId(leadId, stageId, decision.eventName);
        const pageId = a0.page_id || (metaInteg?.config?.page_id as string | undefined) || null;
        const wabaId = await resolveMetaWabaId(pageId, a0, metaInteg);

        const result = await sendMetaConversionEvent({
            eventName: decision.eventName,
            phone: attr.phone,
            datasetId,
            organizationId: orgId,
            pageId,
            wabaId,
            ctwaClid: a0.ctwa_clid ?? undefined,
            firstName: attr.firstName,
            lastName: attr.lastName,
            email: attr.email,
            eventId,
            externalId: attr.contactId,
        });

        if (!result.success || (result.events_received ?? 0) < 1) {
            log.info('CAPI marco falhou', { error: result.error });
            return;
        }

        if (attr.conversationId) {
            const prev = a0.stage_conversions_sent ?? {};
            const nextAttribution: MetaAttribution = {
                ...a0,
                stage_conversions_sent: {
                    ...prev,
                    [stageId]: {
                        event: decision.eventName,
                        at: new Date().toISOString(),
                        event_id: eventId,
                    },
                },
            };
            try {
                await supabase
                    .from('chat_conversations')
                    .update({ meta_attribution: nextAttribution })
                    .eq('id', attr.conversationId)
                    .eq('organization_id', orgId);
            } catch (e) {
                log.warn('Falha ao marcar marco enviado', {
                    name: e instanceof Error ? e.name : 'Error',
                });
            }
        }

        log.info('Marco de funil enviado', {
            event: decision.eventName,
            attributed: result.attributed === true,
        });
    } catch (error: unknown) {
        log.warn('Erro ao enviar marco de funil', {
            name: error instanceof Error ? error.name : 'Error',
        });
    }
}
